// Modules
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { BrowserModule } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { NgModule, APP_INITIALIZER, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { HighchartsChartModule } from 'highcharts-angular';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { primeNGTranslation } from '@shared-module/constants/primeNGTanslation';

// Locales
import localeEsUy from '@angular/common/locales/es-UY';

// Custom Modules
import { SharedModule } from '@shared-module/shared.module';
import { AppRoutingModule } from '@app/app-routing.module';

// Pages
import { LoginComponent } from '@public/pages/login/login.component';
import { PasswordChangeComponent } from '@private/pages/password-change/password-change.component';
import { DashboardWrapperComponent } from '@private/pages/dashboard-wrapper/dashboard-wrapper.component';
import { PasswordRecoveryComponent } from '@public/pages/password-recovery/password-recovery.component';
import { ActivateAccountComponent } from '@public/pages/activate-account/activate-account.component';
import { ViewProfileComponent } from '@private/pages/view-profile/view-profile.component';

// Components
import { AppComponent } from '@app/app.component';
import { PasswordRecoveryDialogComponent } from '@public/components/password-recovery-dialog/password-recovery-dialog.component';
import { LogoutConfirmationDialogComponent } from '@app/general/private/components/logout-confirmation-dialog/logout-confirmation-dialog.component';
import { PasswordRecoverySuccessfulComponent } from '@public/components/password-recovery-successful/password-recovery-successful.component';
import { ChartsComponentComponent } from './general/private/components/charts-component/charts-component.component';
import { InfoDataComponent } from '@app/general/private/components/info-data/info-data.component';
import { PasswordValidationIndicatorsComponent } from './general/private/components/password-validation-indicators/password-validation-indicators.component';
import { ForcedPasswordChangeComponent } from './general/public/pages/forced-password-change/forced-password-change.component';

// Services
import { AppConfigService } from '@shared-module/services/app-config/app-config.service';

// Store
import { AppEffects } from '@store/effects';
import { reducers, metaReducers } from '@store/reducers';

// Environment
import { environment } from '@environment/environment';
import { DashboardComponent } from './general/private/pages/dashboard/dashboard.component';
import { GenericPaginatorComponent } from './modules/shared/components/generic-paginator/generic-paginator.component';
import { ngrxFormsFeature } from '@switch_software/ui-components';
import {
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

// Locales Registration
registerLocaleData(localeEsUy, 'es-UY');

const appConfig = (app: AppConfigService) => {
  return () => {
    return app.loadAppConfig();
  };
};

@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        PasswordChangeComponent,
        DashboardWrapperComponent,
        PasswordRecoveryComponent,
        ActivateAccountComponent,
        PasswordRecoveryDialogComponent,
        LogoutConfirmationDialogComponent,
        PasswordRecoverySuccessfulComponent,
        ChartsComponentComponent,
        DashboardComponent,
        ViewProfileComponent,
        GenericPaginatorComponent,
        InfoDataComponent,
        ForcedPasswordChangeComponent,
    ],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [BrowserModule,
        PasswordValidationIndicatorsComponent,
        AppRoutingModule,
        MsalModule,
        SharedModule.forRoot(),
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
            },
        }),
        StoreModule.forFeature(ngrxFormsFeature.name, ngrxFormsFeature.reducer),
        EffectsModule.forRoot(AppEffects),
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            logOnly: !environment.production,
        }),
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            positionClass: 'toast-top-right',
            preventDuplicates: true,
        }),
        HighchartsChartModule,
        FormsModule,
        MultiSelectModule,
        CheckboxModule], providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        AppConfigService,
        {
            provide: APP_INITIALIZER,
            useFactory: appConfig,
            multi: true,
            deps: [AppConfigService],
        },
        {
            provide: LOCALE_ID,
            useValue: 'es-UY',
        },
        {
            // Angular CDK defaults overlays (MatDialog, MatMenu, etc.) to the native
            // Popover API, which renders them in the browser's top layer — always
            // above regular stacking-context content regardless of z-index. That
            // breaks stacking against non-CDK overlays like PrimeNG's pTooltip.
            // Opt back into the previous z-index-based overlay stacking.
            provide: OVERLAY_DEFAULT_CONFIG,
            useValue: { usePopover: false },
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        MsalGuard,
        MsalService,
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    darkModeSelector: false,
                    colorScheme: 'light',
                    cssLayer: {
                        name: 'primeng',
                        order: 'primeng, app-styles'
                    }
                }
            },
            translation: primeNGTranslation
        }),
    ] })
export class AppModule {}
